import { useState } from 'react';
import { getRouteLogistics } from '../transportDeep';
import { getTransportInfo } from '../transport';

// Scripts the door-to-door travel day between two stops on a multi-city
// trip. Goes WAY deeper than "Lisbon → Porto: train, 3h" — generates a
// minute-by-minute plan covering hotel checkout, bag storage, lunch,
// boarding, in-transit tips, arrival transit, and check-in at the next
// hotel. This is the killer feature for multi-city — it removes the
// "wait, when do I actually leave?" anxiety from every transition.

// Build the day script. Returns ordered timeline entries with time, what,
// where, why, and (when applicable) a link.
function buildTravelDayScript({ fromStop, toStop, route, fallbackTransport, trainNum, depTime }) {
  if (!fromStop || !toStop) return [];

  const entries = [];

  // ── Pre-travel: morning at the from-stop ──
  const checkoutTime = '11:00 AM'; // Standard hotel checkout
  const trainOrFlightDepTime = depTime || '01:30 PM'; // Default afternoon departure if unspecified

  entries.push({
    time: '09:00 AM',
    icon: '🥐',
    label: 'Last breakfast in ' + fromStop.destination.city,
    detail: `Hit a local spot near your hotel — make it the breakfast you've been looking forward to. Tipping isn't expected, but rounding up coffee or leaving €1-2 is appreciated.`,
    category: 'meal',
  });

  entries.push({
    time: checkoutTime,
    icon: '🧳',
    label: `Hotel checkout in ${fromStop.destination.city}`,
    detail: fromStop.hotel
      ? `Check out of ${fromStop.hotel.hotel_name}. Ask front desk to store bags if you need them held past checkout (most properties do this free). If you have Globalist/Diamond/Titanium status, request late checkout — often granted to 1-2 PM.`
      : `Standard checkout time. Most properties hold bags free past checkout — ask front desk.`,
    category: 'logistics',
  });

  // Bag storage (only if there's time between checkout and departure)
  if (route?.bagStorage && route.bagStorage.length > 0) {
    entries.push({
      time: '11:30 AM',
      icon: '🔐',
      label: 'Store bags (if hotel won\'t hold them past mid-day)',
      detail: route.bagStorage[0].name + ' — ' + route.bagStorage[0].distFromStation + ' · ' + route.bagStorage[0].cost + (route.bagStorage[0].notes ? ` (${route.bagStorage[0].notes})` : ''),
      link: route.bagStorage[0].url,
      linkLabel: 'Reserve a spot',
      category: 'logistics',
      altOptions: route.bagStorage.slice(1, 3).map(b => `${b.name} (${b.distFromStation}, ${b.cost})`),
    });
  }

  // Lunch window if leaving in the afternoon
  if (trainOrFlightDepTime.includes('PM') || trainOrFlightDepTime.match(/^(12|0?[1-5]):\d{2}/)) {
    entries.push({
      time: '12:00 PM',
      icon: '🍽️',
      label: 'Light lunch before departure',
      detail: `Don't do a heavy multi-course lunch — long travel days + full stomach = uncomfortable. Aim for petiscos / tapas / sandwich style. Near the station is often touristy — walk 5 min in either direction for better local options.`,
      category: 'meal',
    });
  }

  // Get to the station
  const stationName = route?.originStations?.[0]?.name || `${fromStop.destination.city} main station`;
  const stationNotes = route?.originStations?.[0]?.notes;
  entries.push({
    time: subtractMinutes(trainOrFlightDepTime, 45),
    icon: '🚕',
    label: `Head to ${stationName}`,
    detail: `Taxi is usually the fastest from a city-center hotel (€10-15 in most European cities). Allow 20-30 min for traffic.${stationNotes ? ' Station note: ' + stationNotes : ''}${route?.originStations?.length > 1 ? ` Alt station: ${route.originStations[1].name} (${route.originStations[1].notes})` : ''}`,
    category: 'transit',
  });

  // At the station / pickup bags
  entries.push({
    time: subtractMinutes(trainOrFlightDepTime, 30),
    icon: '🏛️',
    label: `Pick up bags + arrive at ${stationName}`,
    detail: `Arrive at least 30 min ahead for trains, 90 min ahead for short-haul flights, 2-3h for long-haul international. Use the time to grab water + a snack for the journey.`,
    category: 'transit',
  });

  // Boarding
  entries.push({
    time: subtractMinutes(trainOrFlightDepTime, 10),
    icon: '🎫',
    label: `Board ${trainNum ? trainNum : (route?.primaryMode === 'train' ? 'your train' : 'your flight')}`,
    detail: route?.seatTip || 'No specific seat tip — either side is fine.',
    category: 'transit',
  });

  // In-transit
  entries.push({
    time: trainOrFlightDepTime,
    icon: route?.primaryMode === 'train' || route?.primaryMode === 'train_scenic' ? '🚄' : '✈️',
    label: `Depart for ${toStop.destination.city}`,
    detail: route?.inTransitTips ? route.inTransitTips.join(' · ') : (fallbackTransport ? fallbackTransport.note : 'Settle in for the ride. Stretch every 90 min if it\'s a long one.'),
    category: 'transit',
  });

  // Arrival
  const arrTime = route?.duration ? addDuration(trainOrFlightDepTime, route.duration) : '04:30 PM';
  const destStationName = route?.destStations?.[0]?.name || `${toStop.destination.city} main station`;
  entries.push({
    time: arrTime,
    icon: '🚉',
    label: `Arrive ${destStationName}`,
    detail: route?.destStations?.[0]?.notes || 'Welcome to your next stop!',
    category: 'transit',
  });

  // Arrival transit to new hotel
  if (route?.arrivalTransit) {
    const primary = route.arrivalTransit.taxi || route.arrivalTransit.metro || route.arrivalTransit.tube || route.arrivalTransit.walk || route.arrivalTransit.shuttle;
    const altOptions = Object.entries(route.arrivalTransit).filter(([k, v]) => v !== primary).map(([k, v]) => `${k}: ${v.cost} · ${v.time}${v.notes ? ' (' + v.notes + ')' : ''}`);
    entries.push({
      time: addMinutes(arrTime, 10),
      icon: '🚕',
      label: 'Transit to your hotel',
      detail: `${primary.cost} · ${primary.time}${primary.notes ? ' — ' + primary.notes : ''}`,
      category: 'transit',
      altOptions,
    });
  }

  // New hotel check-in
  const checkinTime = addMinutes(arrTime, route?.arrivalTransit?.taxi?.time ? minutesFromTimeString(route.arrivalTransit.taxi.time) : 20);
  entries.push({
    time: checkinTime,
    icon: '🏨',
    label: toStop.hotel ? `Check in: ${toStop.hotel.hotel_name}` : `Check in to your ${toStop.destination.city} hotel`,
    detail: toStop.hotel
      ? `Standard check-in is 3 PM. If you arrive earlier, front desk will store bags + you can use lobby/lounge. Globalist/Diamond status often unlocks early check-in. After settling in, take 30 min to unpack + identify the closest pharmacy / 24h grocery / ATM.`
      : `Standard check-in is 3 PM. If you arrive earlier, hotels hold bags free. Take 30 min after check-in to unpack + scout the immediate area.`,
    category: 'logistics',
  });

  // Light arrival evening
  entries.push({
    time: addMinutes(checkinTime, 60),
    icon: '🚶',
    label: `Light orientation walk + casual dinner`,
    detail: "Don't plan anything ambitious tonight — you've been moving all day. A 30-min walk through the neighborhood near your hotel + a casual dinner is plenty. Save the anchor sights for tomorrow when you're fresh.",
    category: 'meal',
  });

  return entries;
}

// Time helpers — naive but good enough for our hourly granularity.
function subtractMinutes(timeStr, minutes) {
  const [h, m] = parseTime(timeStr);
  let totalMin = h * 60 + m - minutes;
  if (totalMin < 0) totalMin += 24 * 60;
  return formatTime(totalMin);
}
function addMinutes(timeStr, minutes) {
  const [h, m] = parseTime(timeStr);
  let totalMin = h * 60 + m + minutes;
  totalMin = totalMin % (24 * 60);
  return formatTime(totalMin);
}
function addDuration(timeStr, durStr) {
  // Parse "3h" or "2h45m" or "2h15m" into minutes
  let mins = 0;
  const hMatch = durStr.match(/(\d+)h/);
  const mMatch = durStr.match(/(\d+)m/);
  if (hMatch) mins += parseInt(hMatch[1], 10) * 60;
  if (mMatch) mins += parseInt(mMatch[1], 10);
  return addMinutes(timeStr, mins || 180);
}
function parseTime(timeStr) {
  // "01:30 PM" → [hour24, minute]
  const m = timeStr.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
  if (!m) return [12, 0];
  let h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  const period = (m[3] || 'AM').toUpperCase();
  if (period === 'PM' && h !== 12) h += 12;
  if (period === 'AM' && h === 12) h = 0;
  return [h, min];
}
function formatTime(totalMin) {
  const h24 = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  const period = h24 >= 12 ? 'PM' : 'AM';
  let h12 = h24 % 12;
  if (h12 === 0) h12 = 12;
  return `${String(h12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${period}`;
}
function minutesFromTimeString(s) {
  // "8 min" → 8, "20 min" → 20, "1h" → 60
  const hMatch = s.match(/(\d+)\s*h/);
  const mMatch = s.match(/(\d+)\s*min/);
  return (hMatch ? parseInt(hMatch[1], 10) * 60 : 0) + (mMatch ? parseInt(mMatch[1], 10) : 0) || 20;
}

const CATEGORY_COLOR = {
  logistics: 'border-amber-500/30 bg-amber-500/5',
  meal: 'border-gold/30 bg-gold/5',
  transit: 'border-teal/30 bg-teal/5',
};

export default function TravelDayPlanner({ fromStop, toStop, transitionDate }) {
  const [selectedTrainIdx, setSelectedTrainIdx] = useState(0);
  if (!fromStop || !toStop) return null;

  const route = getRouteLogistics(fromStop.destination.city, toStop.destination.city);
  const fallback = !route ? getTransportInfo(fromStop.destination.city, toStop.destination.city) : null;

  // If we have multiple recommended trains, let user toggle which they want planned around
  const trains = route?.recommendedTrains || [];
  const selectedTrain = trains[selectedTrainIdx];
  const depTime = selectedTrain ? convert24To12(selectedTrain.dep) : null;
  const trainNum = selectedTrain ? selectedTrain.number : null;

  const script = buildTravelDayScript({
    fromStop,
    toStop,
    route,
    fallbackTransport: fallback,
    trainNum,
    depTime,
  });

  return (
    <div className="bg-navy-light border border-teal/30 rounded-2xl p-4 sm:p-5 space-y-4">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-base font-bold text-teal">Travel day · {fromStop.destination.city} → {toStop.destination.city}</h3>
          {transitionDate && (
            <div className="text-xs text-brand-ink/65 mt-0.5">
              {transitionDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </div>
          )}
        </div>
        {route && (
          <div className="text-[10px] font-bold px-2 py-1 rounded-full bg-teal/15 text-teal border border-teal/30">
            {route.primaryMode === 'train' ? '🚄 Train' : route.primaryMode === 'train_scenic' ? '🚄 Scenic train' : route.primaryMode === 'train_or_drive' ? '🚄 or 🚗' : '✈️ Flight'} · {route.duration} · {route.cost}
          </div>
        )}
      </div>

      {!route && fallback && (
        <div className="bg-amber-500/10 border border-amber-500/25 rounded-md p-2.5 text-xs text-brand-ink/75">
          We don't have a deep logistics map for this route yet. Based on the transport database: {fallback.mode}, ~{fallback.duration}, {fallback.cost}. {fallback.note} The script below uses sensible defaults.
        </div>
      )}

      {/* Route quick facts */}
      {route && (
        <div className="grid sm:grid-cols-2 gap-2 text-xs">
          <FactCard label="Booking" value={route.company} note={route.bookingLeadTime} link={route.bookingUrl} linkLabel="Buy tickets" />
          <FactCard label="Refunds" value={route.refundable || 'Check booking platform'} />
          {route.dayOfWeekNotes && (
            <div className="sm:col-span-2 bg-amber-500/10 border border-amber-500/25 rounded-md p-2 text-[11px] text-brand-ink/75">
              <strong className="text-amber-300">Day-of-week note:</strong> {route.dayOfWeekNotes}
            </div>
          )}
        </div>
      )}

      {/* Train picker */}
      {trains.length > 0 && (
        <div>
          <div className="text-xs font-bold text-brand-ink/75 mb-1.5">Pick a departure to plan around</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
            {trains.map((t, i) => (
              <button
                key={i}
                onClick={() => setSelectedTrainIdx(i)}
                className={`text-left p-2 rounded-lg border transition ${
                  i === selectedTrainIdx
                    ? 'bg-teal/15 border-teal/40 ring-1 ring-teal/30'
                    : 'bg-brand-ink/5 border-brand-ink/10 hover:border-brand-ink/30'
                }`}
              >
                <div className="text-xs font-bold text-brand-ink">{t.number}</div>
                <div className="text-[10px] text-brand-ink/65">{t.dep} → {t.arr}</div>
                <div className="text-[10px] text-brand-ink/55 leading-tight mt-0.5">{t.notes}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Door-to-door script */}
      <div>
        <div className="text-xs font-bold text-brand-ink/75 mb-1.5">Door-to-door script</div>
        <div className="space-y-1.5">
          {script.map((entry, i) => (
            <div key={i} className={`flex gap-3 rounded-md border p-2.5 ${CATEGORY_COLOR[entry.category] || 'border-brand-ink/15 bg-brand-ink/5'}`}>
              <div className="text-xs font-bold text-brand-ink/75 w-20 shrink-0">{entry.time}</div>
              <div className="text-base shrink-0">{entry.icon}</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-brand-ink">{entry.label}</div>
                <div className="text-[11px] text-brand-ink/70 mt-0.5 leading-snug">{entry.detail}</div>
                {entry.link && (
                  <a
                    href={entry.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block mt-1 text-[11px] font-semibold text-teal hover:underline"
                  >
                    {entry.linkLabel || 'Open'} ↗
                  </a>
                )}
                {entry.altOptions && entry.altOptions.length > 0 && (
                  <div className="mt-1 text-[10px] text-brand-ink/55 italic">
                    Alternatives: {entry.altOptions.join(' · ')}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}

function FactCard({ label, value, note, link, linkLabel }) {
  return (
    <div className="bg-brand-ink/5 border border-brand-ink/10 rounded-md p-2.5">
      <div className="text-[10px] font-bold text-brand-ink/55 uppercase tracking-wider">{label}</div>
      <div className="text-xs text-brand-ink/85 font-medium mt-0.5">{value}</div>
      {note && <div className="text-[10px] text-brand-ink/65 mt-0.5 leading-snug">{note}</div>}
      {link && (
        <a href={link} target="_blank" rel="noopener noreferrer" className="text-[11px] font-semibold text-teal hover:underline mt-1 inline-block">
          {linkLabel || 'Open'} ↗
        </a>
      )}
    </div>
  );
}

function convert24To12(t) {
  // "08:00" → "08:00 AM"
  const [h, m] = t.split(':').map(s => parseInt(s, 10));
  const period = h >= 12 ? 'PM' : 'AM';
  let h12 = h % 12;
  if (h12 === 0) h12 = 12;
  return `${String(h12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${period}`;
}
